import { descriptorPhrase, type SemanticDescriptor } from '@/semantics/SemanticProfile';
import { DEPTH_NEAR_IS_BRIGHT } from './CaptureEncoding';
import type {
  ManifestCreature,
  ManifestGround,
  ManifestPlaceTag,
  ManifestStructure,
  ManifestSubject,
  SceneManifest,
} from './SceneManifest';
import { describeReference, type RenderReference } from './RenderReferences';
import type { StylePreset } from './StylePresets';

/**
 * Turns a {@link SceneManifest} into the prompt sent alongside the three captured images.
 *
 * The prompt has one job the images cannot do: **bind names to silhouettes**. The ID-map image says
 * "this cluster of pixels is one thing"; the legend here says "that thing is Acer rubrum, 5.4 m, in
 * autumn scarlet". Together they remove the guesswork that otherwise turns a designed planting into
 * a generic garden.
 *
 * Pure and deterministic — the same manifest and preset always yield the same text, so a render can
 * be reproduced, and the output is shown in an editable field before anything is sent.
 */

export interface EnhancePromptOptions {
  /** Extra art direction typed by the user, appended verbatim. */
  extraDirection?: string;
  /** Include the depth map's description. Set false when only two images are sent. */
  includeDepth?: boolean;
  /** Include the ID map's legend. Set false when only the beauty render is sent. */
  includeIdMap?: boolean;
  /**
   * How many designer-supplied reference photographs follow the captured passes.
   *
   * Counted rather than described, because these arrive with the render request and speak for
   * themselves; the prompt's only job is to number them correctly — a reference the legend never
   * mentions is read as another view of the site and gets composited into the output.
   */
  referenceCount?: number;
  /**
   * Reference photographs that came off semantic tags, and therefore know what they are about.
   *
   * Described rather than counted, and the difference is the whole point: the anonymous wording above
   * has to forbid nearly everything to stop a second garden being pasted into the frame, which also
   * forbids the one thing a picture of a fence was attached to do. A tagged reference states which
   * silhouette it governs, so it can be allowed to govern it. See `RenderReferences.ts`.
   *
   * These are uploaded FIRST, immediately after the captured passes, so their numbering is stable
   * whether or not the designer also attached something ad-hoc to this particular render.
   */
  references?: readonly RenderReference[];
}

export interface EnhancePrompt {
  /** The full prompt text. */
  text: string;
  /** Legend lines, also surfaced in the UI so the mapping is inspectable. */
  legend: string[];
}

export function buildEnhancePrompt(
  manifest: SceneManifest,
  preset: StylePreset,
  options: EnhancePromptOptions = {},
): EnhancePrompt {
  const includeIdMap = options.includeIdMap ?? true;
  const includeDepth = options.includeDepth ?? true;

  const legend = includeIdMap ? buildLegend(manifest) : [];
  const sections: string[] = [];

  // Built before the input legend, not where it is used: the description of image 2 has to say
  // whether there is a ground list to consult, and pointing at a section that was never emitted is
  // worse than not mentioning one.
  const ground = describeGround(manifest);

  sections.push(preset.styleClause);
  sections.push(preset.cameraClause);

  const referenceCount = Math.max(0, Math.trunc(options.referenceCount ?? 0));
  const references = options.references ?? [];

  sections.push(
    describeInputs(includeIdMap, includeDepth, ground !== null, referenceCount, references),
  );
  sections.push(STRUCTURE_LOCK);

  sections.push(describeConditions(manifest));

  // Site-wide intent comes before anything specific: it is the register every later line should be
  // read inside, and a style stated after fourteen planting descriptions arrives too late to colour
  // them.
  const intent = describeSiteIntent(manifest);
  if (intent !== null) sections.push(intent);

  if (includeIdMap && legend.length > 0) {
    sections.push(
      ['PLANT KEY — each colour in image 2 marks exactly these plants:', ...legend].join('\n'),
    );
  }

  // A separate key from the planting one, not extra rows in it. A model reading "#80b020 = Eastern
  // cottontail" under a heading that says PLANT KEY has been told a rabbit is a plant, and it draws
  // one accordingly.
  const creatureKey = includeIdMap ? buildCreatureLegend(manifest) : [];
  if (creatureKey.length > 0) {
    sections.push(
      [
        'LIVING SUBJECTS KEY — each colour in image 2 marks exactly these animals or people:',
        ...creatureKey,
      ].join('\n'),
    );
  }

  // Built elements come before the planting: they are the largest objects in frame, and an
  // unmentioned building is the single thing a generative edit is most likely to reinvent.
  const built = describeStructures(manifest);
  if (built !== null) sections.push(built);

  // Before the planting for a different reason: a creature is the smallest thing in frame and the
  // most easily lost, and by this point the model has been told twice to change nothing. It needs to
  // read what the grey mass on the lawn actually is before it decides the mass is an artefact.
  const living = describeCreatures(manifest);
  if (living !== null) sections.push(living);

  const subjects = describeSubjects(manifest);
  if (subjects.length > 0) {
    sections.push(['PLANTING, front of frame to back:', ...subjects].join('\n'));
  }

  const extras = describeAdditional(manifest);
  if (extras !== null) sections.push(extras);

  if (ground !== null) sections.push(ground);

  const places = describePlaces(manifest);
  if (places !== null) sections.push(places);

  // Paired with the refusal below, and collected for the mirror-image reason: a requirement buried in
  // the trailing clause of the ninth planting line reads as trim, however loudly it was tagged.
  const required = collectRequired(manifest);
  if (required.length > 0) {
    sections.push(
      [
        'MUST BE VISIBLE — the designer stated these explicitly. Each is a required part of this scene and overrides any generic reading of the shape it belongs to. Render every one of them, on the thing named, even where image 1 shows only plain geometry:',
        ...required.map((line) => `- ${line}`),
      ].join('\n'),
    );
  }

  // Collected last and stated as a hard refusal, because a negative buried among descriptions of
  // things to draw reads as one more thing to draw.
  const forbidden = collectForbidden(manifest);
  if (forbidden.length > 0) {
    sections.push(
      [
        'DO NOT INCLUDE — the designer has explicitly ruled these out:',
        ...forbidden.map((line) => `- ${line}`),
      ].join('\n'),
    );
  }

  sections.push(`Avoid: ${preset.negativeClause}`);

  const extra = options.extraDirection?.trim();
  if (extra) sections.push(`Additional direction: ${extra}`);

  return { text: sections.join('\n\n'), legend };
}

/**
 * The single most important instruction. An image edit is generative, so without an explicit lock
 * the model happily relocates a specimen tree or invents a path. Stated plainly and early.
 *
 * The closing exception is not a softening of it. Without one the lock and the semantic layer flatly
 * contradict each other — "add nothing" against "this spruce is a decorated Christmas tree" — and a
 * refusal stated in capitals near the top of the prompt beats a clause at the end of a planting line
 * every time. So the lock is narrowed to what it was always actually for: nothing the designer did
 * not ask for. What they *did* ask for is named below, and named again under MUST BE VISIBLE.
 */
const STRUCTURE_LOCK =
  'CRITICAL — this is a re-rendering, not a redesign. Keep the composition pixel-faithful: do not move, resize, add, remove, or substitute any plant, animal, person, building, paved area, water body, or painted ground surface. Preserve every silhouette, the exact horizon line, the ground plane, the camera angle, and the spacing between plants. Every ground surface keeps its exact extent and outline: a stream, pond, path or bed occupies precisely the pixels it occupies in image 1, and must not be narrowed, straightened, shortened, or grassed over. Change only material realism, lighting quality, surface detail, and artistic treatment. The number of plants in your output must equal the number in image 1, and so must the number of animals and people. Do not invent any building, fence, path, animal, person, or feature that is not listed below. ONE EXCEPTION: where a description below says what something is, what it is made of, or what it carries, that is a statement of fact about this scene and not an observation of image 1 — render it as described even where image 1 shows only plain untextured geometry, adding the specific detail, ornament, fixture, finish, or living subject it names on the silhouette it names. That is the only thing you may add.';

function describeInputs(
  includeIdMap: boolean,
  includeDepth: boolean,
  hasGround: boolean,
  referenceCount: number,
  references: readonly RenderReference[],
): string {
  const lines = [
    'You are given these reference images:',
    '- Image 1 — the source render. This defines the composition and is what you are re-rendering.',
  ];
  if (includeIdMap) {
    // The ground caveat is the fix for a specific failure: ground is black in the ID map by design,
    // and a model reading black as "nothing here" will flatten a stream or a gravel court into lawn.
    // Where a ground list exists it is named as the authority; where none was sampled the caveat
    // still has to be made, so it falls back to image 1 rather than citing a section that is absent.
    const groundNote = hasGround
      ? 'Black is ground and sky — the absence of colour says nothing about what the ground is, so read the ground from image 1 and the GROUND SURFACES list below, never from image 2.'
      : 'Black is ground and sky — the absence of colour says nothing about what the ground is, so read the ground entirely from image 1 and preserve whatever surfaces it shows.';
    lines.push(
      `- Image 2 — a semantic map of the same frame. Each flat colour marks every pixel belonging to one plant species, one built element, or one kind of animal or person, listed in the keys below. ${groundNote} Use image 2 to identify what each shape in image 1 actually is; do not reproduce its colours in your output.`,
    );
  }
  if (includeDepth) {
    // Polarity comes from `CapturePasses.DEPTH_NEAR_IS_BRIGHT`; changing one without the other
    // would invert the model's sense of depth.
    lines.push(
      `- Image 3 — a depth map of the same frame; ${
        DEPTH_NEAR_IS_BRIGHT ? 'brighter is nearer the camera and black is sky' : 'darker is nearer the camera and white is sky'
      }. Use it for atmospheric perspective, haze, and depth of field.`,
    );
  }
  // Numbered off the passes actually sent, not off a fixed 4: with the ID map or depth omitted the
  // references shift down, and a legend that names image 4 when the upload arrived as image 3 is
  // worse than no legend at all. Tagged references come first, so adding an ad-hoc photograph to one
  // render never renumbers the fence.
  let next = 1 + (includeIdMap ? 1 : 0) + (includeDepth ? 1 : 0) + 1;

  for (const reference of references) {
    lines.push(describeReference(reference, next));
    next += 1;
  }

  if (referenceCount > 0) {
    const first = next;
    const last = first + referenceCount - 1;
    const label = referenceCount === 1 ? `Image ${first}` : `Images ${first}–${last}`;
    const noun = referenceCount === 1 ? 'a photograph' : 'photographs';
    // Stated as a mood board rather than as content, because the failure this guards against is
    // specific and severe: an image model handed a second garden will happily paste its pergola,
    // its planting, or its whole background into a frame that has none of those things.
    lines.push(
      `- ${label} — ${noun} supplied by the designer as visual reference for how this should look. Read ${referenceCount === 1 ? 'it' : 'them'} for material, texture, finish, planting character, colour palette, and mood only. ${referenceCount === 1 ? 'It is' : 'They are'} NOT part of the scene: do not copy ${referenceCount === 1 ? 'its' : 'their'} composition, camera angle, buildings, plants, or background into your output, and do not add any object from ${referenceCount === 1 ? 'it' : 'them'} that image 1 does not already contain. Image 1 remains the only source of what is in this frame and where.`,
    );
  }
  return lines.join('\n');
}

function describeConditions(manifest: SceneManifest): string {
  const env = manifest.environment;
  const lines = [
    'SCENE CONDITIONS:',
    `- Season and date: ${env.seasonLabel}, ${env.dateLabel}. The planting must read unmistakably as ${env.seasonLabel}.`,
    `- Time and light: ${env.timeLabel} — ${env.lightPhrase}.`,
    `- Weather: ${formatWeather(env.weather)}.`,
    `- Vantage: ${manifest.camera.phrase}.`,
  ];
  if (env.groundCover !== null) {
    // Placed before the vantage-independent site line and stated as a covering rather than as a
    // material, because the ground materials are listed separately and the model must understand
    // that the paving is still there under the snow rather than replaced by it.
    lines.push(
      `- Ground cover: the whole site is under ${env.groundCover}. Every ground material listed below is still there, covered — paths, beds and paving read through it as form, not as exposed surface.`,
    );
  }
  if (manifest.site !== null) {
    lines.push(
      `- Site: a ${Math.round(manifest.site.widthMeters)} m × ${Math.round(manifest.site.lengthMeters)} m ${manifest.site.templateName.toLowerCase()} garden.`,
    );
  }
  return lines.join('\n');
}

function formatWeather(weather: string): string {
  const labels: Record<string, string> = {
    clear: 'clear sky',
    overcast: 'overcast, flat diffuse light',
    'golden-hour': 'golden hour, warm and hazy',
    'light-wind': 'clear with light air',
    breezy: 'clear and breezy',
    'after-rain': 'just after rain — wet foliage, damp soil, glistening highlights, puddles on hard surfaces',
  };
  return labels[weather] ?? weather;
}

function buildLegend(manifest: SceneManifest): string[] {
  return manifest.legend.map((entry) => {
    const plural = entry.count === 1 ? '1 plant' : `${entry.count} plants`;
    return `  ${entry.keyColor} = ${entry.botanicalName} (${entry.commonName}) — ${plural}`;
  });
}

function describeSubjects(manifest: SceneManifest): string[] {
  return manifest.subjects.map((subject) => `- ${subjectLine(subject)}`);
}

/* ------------------------------------ Semantics ------------------------------------ */

/**
 * The semantic layer, in prompt form.
 *
 * Everything below exists because the rest of this file describes a scene with total precision and no
 * meaning. "Panicum virgatum, 1.2 m, arching, right foreground" tells a model exactly what shape to
 * draw and nothing about what the picture is *of* — and a re-render of a garden is a picture of
 * something. Tags supply that, and this is where they enter the sentence.
 *
 * Two rules run through all of it. Positives are woven into the line that already binds a description
 * to a silhouette, because a fact about a shape is only usable when the model knows which shape.
 * Negatives are pulled out into one refusal at the end, because an image model reading "no furniture"
 * inside a list of things to render will draw furniture.
 */

/** Positive descriptors only — the "avoid" ones are collected separately and stated as a refusal. */
function positives(descriptors: readonly SemanticDescriptor[]): SemanticDescriptor[] {
  return descriptors.filter((descriptor) => descriptor.category !== 'avoid');
}

/**
 * Semantics as a clause appended to a subject or structure line.
 *
 * Capped, and deliberately: a species with a dozen catalog tags would otherwise bury the dimensions
 * and the key colour that make the line work at all. Manual tags sort first inside a profile, so the
 * cap drops catalog trivia rather than anything the designer typed.
 */
function semanticClause(descriptors: readonly SemanticDescriptor[], max = 5): string | null {
  const picked = positives(descriptors).slice(0, max);
  if (picked.length === 0) return null;
  return picked.map(descriptorPhrase).join('; ');
}

/** Whole-site tags: the register the entire render sits inside. */
function describeSiteIntent(manifest: SceneManifest): string | null {
  const picked = positives(manifest.siteSemantics);
  if (picked.length === 0) return null;
  return [
    'DESIGN INTENT — what this garden is. Every choice below about material, light, and finish should serve this:',
    ...picked.map((descriptor) => `- ${descriptorPhrase(descriptor)}`),
  ].join('\n');
}

/**
 * The ground materials, largest share first.
 *
 * Stated as ground *cover*, with a percentage, because the model has no other way to know: the ID map
 * paints ground black by design, so the second image says nothing about it, and the beauty pass shows
 * only a flat colour. Without this section a painted gravel courtyard reaches the model as a grey
 * field and comes back as lawn.
 */
function describeGround(manifest: SceneManifest): string | null {
  if (manifest.ground.length === 0) return null;
  const lines = manifest.ground.map((entry) => `- ${groundLine(entry)}`);
  return [
    'GROUND SURFACES — the ground in this frame is made of these materials, with the share of visible ground each covers. Image 2 paints all ground black, so this list is the only statement of what the ground actually is: read its extent from image 1 and its identity from here. Render each as the real surface it names, with the joints, texture, reflectivity, and wear that surface actually has. Every one of them must be present in your output at the same extent — do not substitute lawn for water, a hard surface, or a planting bed, or the reverse:',
    ...lines,
  ].join('\n');
}

/**
 * Coverage is rounded up, never to zero.
 *
 * A pond at 0.6 % of the visible ground is small in area and enormous in meaning, and printing it as
 * "0% of the ground in view" tells the model to ignore the one thing this line exists to protect.
 */
function groundLine(entry: ManifestGround): string {
  const percent = Math.max(1, Math.round(entry.coverage * 100));
  const share = `about ${percent}% of the ground in view`;
  const clause = semanticClause(entry.semantics, 4);
  return clause === null ? `${entry.name} — ${share}` : `${entry.name} (${share}) — ${clause}`;
}

/**
 * Tagged places.
 *
 * A place tag is the only semantic with no object of its own, so its line has to do the binding
 * itself: where in frame, how wide, and what is standing in it. Named after the planting rather than
 * before it, so by the time the model reads "the fire-pit lounge" it already knows which shapes are
 * being gathered under that name.
 */
function describePlaces(manifest: SceneManifest): string | null {
  const places = manifest.places.filter((place) => place.category !== 'avoid');
  if (places.length === 0) return null;
  return [
    'PLACES IN THIS VIEW — areas of the garden the designer has named. Render each so its purpose is legible: the furniture, wear, surface finish and staging that place would really have:',
    ...places.map((place) => `- ${placeLine(place)}`),
  ].join('\n');
}

function placeLine(place: ManifestPlaceTag): string {
  const parts = [`${capitalise(place.screen.zone)}:`, `“${place.label}”`];
  parts.push(`— about ${formatMeters(place.radiusMeters * 2)} across`);
  if (place.note !== null) parts.push(`— ${place.note}`);
  if (place.subjectNames.length > 0) {
    parts.push(`— contains ${place.subjectNames.join(', ')}`);
  }
  if (place.emphasis === 'critical') parts.push('— ESSENTIAL: the render is wrong without this reading clearly');
  else if (place.emphasis === 'emphasise') parts.push('— make this clearly readable');
  return parts.join(' ');
}

/**
 * Every typed tag the designer marked louder than "mention", wherever it was attached, with the thing
 * it applies to.
 *
 * The exact mirror of {@link collectForbidden}, and it exists for the same reason. A tag stays on the
 * line that binds it to a silhouette — that is the only place the binding can live — but a line that
 * begins with a botanical name, three measurements, a habit and a foliage colour has spent all its
 * authority by the time it reaches "decorated for Christmas", and the model reads what follows as
 * trim. Repeating the loud ones here, under a heading that says they are requirements, is what makes
 * the difference between a tag that is present in the prompt and a tag that is obeyed.
 *
 * Manual tags only. A catalog slug is a property of the species, true of every specimen and never
 * something the designer decided; promoting `evergreen` and `windbreak` into a list of requirements
 * would bury the one line that was actually typed under a dozen that were not.
 */
function collectRequired(manifest: SceneManifest): string[] {
  const lines: string[] = [];
  const push = (what: string, descriptors: readonly SemanticDescriptor[]): void => {
    for (const descriptor of descriptors) {
      if (descriptor.source !== 'manual') continue;
      if (descriptor.category === 'avoid') continue;
      // Identity is here at any volume, and only identity is. The category exists for the one case
      // where the geometry would otherwise be read as the wrong thing — "this spruce is a decorated
      // Christmas tree" — which is precisely the statement that needs restating where the lock can
      // see it, and emphasis defaults to `mention`, so requiring a loud tag would miss most of them.
      if (descriptor.emphasis === 'mention' && descriptor.category !== 'identity') continue;
      lines.push(`${what}: ${descriptorPhrase(descriptor)}`);
    }
  };

  push('Throughout this view', manifest.siteSemantics);
  for (const structure of manifest.structures) push(`The ${structure.name.toLowerCase()}`, structure.semantics);
  for (const creature of manifest.creatures) {
    push(`The ${creature.name.toLowerCase()} in the ${creature.screen.zone}`, creature.semantics);
  }
  for (const subject of manifest.subjects) {
    // Named by what the reader can pick out of the frame — the botanical name and the zone are what
    // the planting line and the ID map both key on, so the two descriptions meet on the same object.
    const where = `${subject.botanicalName} in the ${subject.screen.zone}`;
    push(subject.count > 1 ? `The ${subject.count} ${where}` : `The ${where}`, subject.semantics);
  }
  for (const entry of manifest.ground) push(`The ${entry.name.toLowerCase()}`, entry.semantics);
  for (const place of manifest.places) {
    if (place.category === 'avoid' || place.emphasis === 'mention') continue;
    const note = place.note !== null ? ` — ${place.note}` : '';
    lines.push(`The ${place.screen.zone}: “${place.label}”${note}`);
  }

  return [...new Set(lines)];
}

/**
 * Every "keep out" tag on the site, wherever it was attached, with the place it applies to.
 *
 * Gathered from all four scopes into one list because a negative is only obeyed when it is
 * unambiguous, and an image model asked to not draw something in the middle of a paragraph about
 * what to draw will reliably do the opposite.
 */
function collectForbidden(manifest: SceneManifest): string[] {
  const lines: string[] = [];
  const push = (where: string, descriptors: readonly SemanticDescriptor[]): void => {
    for (const descriptor of descriptors) {
      if (descriptor.category !== 'avoid') continue;
      const note = descriptor.note !== undefined ? ` (${descriptor.note})` : '';
      lines.push(`${where}: ${descriptor.label}${note}`);
    }
  };

  push('Anywhere in this view', manifest.siteSemantics);
  for (const entry of manifest.ground) push(`On ${entry.name.toLowerCase()}`, entry.semantics);
  for (const structure of manifest.structures) push(`At ${structure.name.toLowerCase()}`, structure.semantics);
  for (const creature of manifest.creatures) push(`Around the ${creature.name.toLowerCase()}`, creature.semantics);
  for (const subject of manifest.subjects) push(`Around the ${subject.commonName}`, subject.semantics);
  for (const place of manifest.places) {
    if (place.category !== 'avoid') continue;
    const note = place.note !== null ? ` (${place.note})` : '';
    lines.push(`In the ${place.screen.zone}: ${place.label}${note}`);
  }

  return [...new Set(lines)];
}

/**
 * Built elements: what they are, how big, where, and which key colour marks them.
 *
 * The renderer can only offer these as plain coloured volumes — the tool models terrain and plants,
 * not architecture. That is precisely why the description carries the weight: given the dimensions,
 * roof form, and materials in words, an image model renders a convincing shed *at the correct
 * footprint and occlusion*, which is the part it cannot infer and the part that matters.
 */
function describeStructures(manifest: SceneManifest): string | null {
  if (manifest.structures.length === 0) return null;

  const lines = manifest.structures.map((structure) => `- ${structureLine(structure)}`);
  return [
    'BUILT ELEMENTS — render these as real architecture and hard landscaping, not as the flat blocks shown in image 1. Each is marked by its own colour in image 2. Keep every footprint, height, and position exactly as shown:',
    ...lines,
  ].join('\n');
}

/**
 * Creatures and figures: what they are, how big, and that they are alive.
 *
 * The hardest section in this file to get obeyed, and the reason is structural. Everything else
 * described here is something the renderer draws convincingly enough that the model's job is to
 * improve it. A creature is drawn as an untextured silhouette at the correct size and nothing more,
 * so the model's honest reading of image 1 is "a grey lump on the lawn" — and its options are to
 * render a grey lump, to smooth it into the grass, or to work out what was meant. Only the third is
 * right, and only this section can produce it.
 *
 * Hence the wording: the size is stated as a fact to check against, the lump is named explicitly as
 * a placeholder rather than left for the model to notice, and the whole set is declared present. A
 * milder phrasing — "there is a deer in the midground" — loses to a structure lock that has already
 * said, in capitals, to change nothing and add nothing.
 */
function describeCreatures(manifest: SceneManifest): string | null {
  if (manifest.creatures.length === 0) return null;

  const lines = manifest.creatures.map((creature) => `- ${creatureLine(creature)}`);
  const total = manifest.visibleCreatureCount;
  const count = total === 1 ? 'one living subject' : `${total} living subjects`;

  return [
    `LIVING SUBJECTS — this scene contains ${count}: real animals and people, each marked by its own colour in image 2. Image 1 draws each of them as a plain untextured placeholder mass at the correct size and position — that mass is scaffolding, not the design. Replace each one with a photographic rendering of the animal or person it stands for, at exactly the same size, position, footing and facing, casting the shadow that thing would cast. Do not smooth one away, do not turn one into a rock, a shrub, a statue, or a garden ornament, and do not add any animal or person beyond those listed here:`,
    ...lines,
  ].join('\n');
}

function creatureLine(creature: ManifestCreature): string {
  const parts = [`${capitalise(creature.screen.zone)}:`, creature.phrase];

  if (creature.scientificName !== undefined) {
    parts.push(`(${creature.scientificName})`);
  }

  const meaning = semanticClause(creature.semantics, 6);
  if (meaning !== null) parts.push(`— ${meaning}`);

  parts.push(`[${creature.keyColor}]`);
  return parts.join(' ');
}

function buildCreatureLegend(manifest: SceneManifest): string[] {
  return manifest.creatureLegend.map((entry) => {
    const noun = entry.faunaClass === 'person' ? 'figure' : 'animal';
    const plural = entry.count === 1 ? `1 ${noun}` : `${entry.count} ${noun}s`;
    const latin = entry.scientificName !== undefined ? ` (${entry.scientificName})` : '';
    return `  ${entry.keyColor} = ${entry.name}${latin} — ${plural}`;
  });
}

function structureLine(structure: ManifestStructure): string {
  const parts = [`${capitalise(structure.screen.zone)}:`, structure.name.toLowerCase()];
  parts.push(`— ${structure.phrase}`);
  if (structure.kind === 'paving') {
    parts.push('— flush with the surrounding ground, not raised');
  }
  // A prop's phrase is only its envelope — the renderer draws an authored mesh or a stand-in box, and
  // neither says what the thing is. Its semantics carry the whole identity, so they are stated first
  // here rather than appended as trimming.
  const meaning = semanticClause(structure.semantics, structure.kind === 'prop' ? 6 : 4);
  if (meaning !== null) parts.push(`— ${meaning}`);
  parts.push(`[${structure.keyColor}]`);
  return parts.join(' ');
}

/**
 * One planting line. Order matters: where it is, what it is, how big, what it looks like today.
 * The key colour is repeated inline so the binding survives even if the legend is skimmed.
 */
function subjectLine(subject: ManifestSubject): string {
  const parts: string[] = [];

  parts.push(`${capitalise(subject.screen.zone)}:`);

  if (subject.count > 1) {
    parts.push(`a drift of ${subject.count} ${subject.botanicalName}`);
  } else {
    const cultivar = subject.cultivar !== undefined ? ` '${subject.cultivar}'` : '';
    parts.push(`${subject.botanicalName}${cultivar}`);
  }
  parts.push(`(${subject.commonName}, ${formatCategory(subject.category)})`);
  parts.push(`— ${formatMeters(subject.heightMeters)} tall × ${formatMeters(subject.spreadMeters)} wide`);

  if (isTreeForm(subject) && subject.canopyBaseMeters > 0.4) {
    parts.push(`with a clear trunk to ${formatMeters(subject.canopyBaseMeters)}`);
  }

  parts.push(`— ${describeHabit(subject)}`);

  if (subject.appearance.phrase.length > 0) {
    parts.push(`— ${subject.appearance.phrase}`);
  }
  if (subject.pruningStyle !== 'natural') {
    parts.push(`— pruned ${subject.pruningStyle}`);
  }

  // Meaning last, before the key colour: the measurements above are what the model checks the shape
  // against, and pushing them down the line behind a list of tags is how a description stops being
  // read as a specification.
  const meaning = semanticClause(subject.semantics);
  if (meaning !== null) parts.push(`— ${meaning}`);

  parts.push(`[${subject.keyColor}]`);

  return parts.join(' ');
}

/**
 * Growth habit from the geometry form. The renderer already used this to decide the silhouette, so
 * naming it keeps the model's interpretation aligned with the shape it can see.
 */
function describeHabit(subject: ManifestSubject): string {
  const habits: Record<string, string> = {
    'round-canopy-tree': 'a broad rounded canopy on a single trunk',
    'conical-tree': 'a dense conical conifer with whorled tiers to the ground',
    'mounding-shrub': 'a multi-stemmed mounding shrub',
    'grass-tuft': 'an arching fountain-shaped grass clump',
    'perennial-clump': 'an upright herbaceous clump',
    'groundcover-mat': 'a low spreading mat hugging the ground',
  };
  return habits[subject.geometryForm] ?? 'a naturally formed plant';
}

function isTreeForm(subject: ManifestSubject): boolean {
  return subject.geometryForm === 'round-canopy-tree' || subject.geometryForm === 'conical-tree';
}

function describeAdditional(manifest: SceneManifest): string | null {
  if (manifest.additional.length === 0) return null;
  const lines = manifest.additional.map(
    (entry) => `  - ${entry.count} × ${entry.botanicalName} (${entry.commonName}) [${entry.keyColor}]`,
  );
  return ['ALSO IN FRAME, further back and smaller:', ...lines].join('\n');
}

function formatCategory(category: string): string {
  return category.replace(/-/g, ' ');
}

function formatMeters(value: number): string {
  return value < 10 ? `${value.toFixed(1)} m` : `${Math.round(value)} m`;
}

function capitalise(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}
